import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/helpers/cn';

import type { ComponentPropsWithoutRef, ElementType } from 'react';

/**
 * Único ponto de tipografia do projeto. Use `<Text>` em vez de `text-*` /
 * `font-*` soltos — classes de tipografia espalhadas são a razão de um projeto
 * acabar com sete tamanhos de "texto pequeno" (docs/03-componentes-e-ui.md).
 *
 * Os eixos são ortogonais de propósito: `variant` controla tamanho/peso,
 * `tone` controla cor. Uma variante `titleMuted` faria a matriz explodir —
 * cada cor nova multiplicaria por cada tamanho.
 *
 * @example
 * <Text variant="pageTitle" as="h1">Pedidos de socorro</Text>
 * <Text tone="muted">Nenhum pedido ativo na sua região.</Text>
 * <Text variant="caption" as="span" truncate>{endereco}</Text>
 */
export const textVariants = cva('', {
  variants: {
    variant: {
      pageTitle: 'text-2xl leading-tight font-bold tracking-tight',
      metric: 'text-3xl leading-none font-bold tabular-nums tracking-tight',
      title: 'text-base leading-snug font-semibold',
      subtitle: 'text-sm leading-snug font-medium',
      body: 'text-base leading-normal',
      caption: 'text-xs leading-normal',
      label: 'text-sm leading-none font-medium',
      overline: 'text-xs leading-none font-semibold tracking-wide uppercase',
    },
    tone: {
      default: '',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      destructive: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    truncate: {
      true: 'truncate',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'body',
    tone: 'default',
    truncate: false,
  },
});

type TextOwnProps<T extends ElementType> = {
  /**
   * Elemento renderizado. Default `p`.
   *
   * Não é derivado da `variant` de propósito: o nível de heading depende de
   * onde o texto está na página, não de quanto ele mede. Um `title` pode ser
   * `h2` num card e `h3` dentro de uma seção — mapear automático acertaria
   * metade das vezes e produziria hierarquia quebrada na outra metade.
   */
  as?: T;
} & VariantProps<typeof textVariants>;

type TextProps<T extends ElementType> = TextOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof TextOwnProps<T>>;

export function Text<T extends ElementType = 'p'>({
  as,
  variant,
  tone,
  align,
  truncate,
  className,
  ...props
}: TextProps<T>) {
  const Component = as ?? 'p';

  return (
    <Component
      className={cn(textVariants({ variant, tone, align, truncate }), className)}
      {...props}
    />
  );
}
